/**
 * Click stitch review entry-point widgets — issue #963 track 3.
 *
 * Peers of the URL `StitchReviewEntry` widgets (#818). The UX
 * principle is that the click→fetch layer is just as visible as
 * the call→route layer:
 *
 *   - URL stitches:   API caller  →  API route  (#812 / #818)
 *   - Click stitches: UI handler  →  API caller (this file, #963)
 *
 * Both are surfaced from the same hosts (ProjectSummary CTA strip,
 * E2EFlowsPage toolbar). When neither has work, the page is quieter;
 * when either does, the user can drill into the matching review UI.
 *
 * Two presentations of the same underlying counts:
 *   - `ClickStitchReviewCard`  — full card for ProjectSummary
 *   - `ClickStitchReviewBadge` — compact button for E2EFlowsPage toolbar
 *
 * Both fetch lazily, fail silently, render nothing pre-analysis.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

interface ClickStitchCounts {
  unstitched: number;
  aiProposals: number;
  state: 'ready' | 'no-analysis';
}

function useClickStitchCounts(
  projectId: string | undefined,
): ClickStitchCounts | null {
  const [counts, setCounts] = useState<ClickStitchCounts | null>(null);
  useEffect(() => {
    if (!projectId) {
      setCounts(null);
      return;
    }
    let cancelled = false;
    api
      .getClickStitchReview(projectId)
      .then(data => {
        if (cancelled) return;
        setCounts({
          // #975: use the true total, not the page slice length.
          // Older API responses don't have totalUnstitched yet —
          // fall back to the slice length for backward compat.
          unstitched: data.totalUnstitched ?? data.unstitched?.length ?? 0,
          aiProposals: data.aiStitches?.length ?? 0,
          state: data.state,
        });
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  return counts;
}

/**
 * Banner-style CTA for ProjectSummary, matching the visual shape of
 * the URL stitch CTA so the two render as true peers (#970 review).
 * Hidden when there's no work — same gate as the URL CTA — so the
 * empty steady state shows nothing rather than a stale "all clear"
 * banner.
 */
export function ClickStitchReviewCard({ projectId }: { projectId: string | undefined }) {
  const counts = useClickStitchCounts(projectId);
  if (!projectId || !counts) return null;

  // "To-review" total = unstitched handlers + pending AI proposals.
  // Confident auto stitches are already applied (#967); they don't
  // count as work for the user.
  const total = counts.unstitched + counts.aiProposals;
  if (total === 0) return null;

  return (
    <Link
      to={`/projects/${projectId}/click-stitch-review`}
      data-testid="click-stitch-review-card"
      className="flex items-center gap-2 px-4 py-2.5 border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
    >
      <svg
        className="w-4 h-4 text-purple-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 15l-2 5L9 9l11 4-5 2z"
        />
      </svg>
      <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
        {total} click stitch{total === 1 ? '' : 'es'} to review
      </span>
      <span className="text-xs text-purple-700/70 dark:text-purple-300/70 ml-auto">
        {counts.unstitched} unstitched · {counts.aiProposals} AI proposals
      </span>
    </Link>
  );
}

export function ClickStitchReviewBadge({ projectId }: { projectId: string | undefined }) {
  const counts = useClickStitchCounts(projectId);
  if (!projectId || !counts) return null;
  const total = counts.unstitched + counts.aiProposals;
  if (total === 0) return null;

  return (
    <Link
      to={`/projects/${projectId}/click-stitch-review`}
      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-lg transition-colors border border-purple-200 dark:border-purple-700"
      title={`${counts.unstitched} unstitched click handlers, ${counts.aiProposals} AI proposals`}
      data-testid="click-stitch-review-badge"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 15l-2 5L9 9l11 4-5 2z"
        />
      </svg>
      {total} click stitch{total === 1 ? '' : 'es'} to review
    </Link>
  );
}
